/**
 * Performance Review Hooks
 *
 * React Query hooks for Performance Lab Pro™ Performance Review API.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PerformanceReviewApi,
  type EligibilityStatus,
  type PerformanceReview,
} from "@nordex/core";

// Query keys
const REVIEW_KEYS = {
  all: ["performance-review"] as const,
  eligibility: () => [...REVIEW_KEYS.all, "eligibility"] as const,
  current: () => [...REVIEW_KEYS.all, "current"] as const,
  history: () => [...REVIEW_KEYS.all, "history"] as const,
};

/**
 * Hook to check eligibility for generating a review
 */
export function useReviewEligibility(userId: string | undefined) {
  return useQuery({
    queryKey: REVIEW_KEYS.eligibility(),
    queryFn: () => PerformanceReviewApi.checkEligibility(),
    enabled: !!userId,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get current month's review
 */
export function useCurrentReview(userId: string | undefined) {
  return useQuery({
    queryKey: REVIEW_KEYS.current(),
    queryFn: () => PerformanceReviewApi.getCurrentReview(),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to get review history
 */
export function useReviewHistory(userId: string | undefined, limit = 12) {
  return useQuery({
    queryKey: [...REVIEW_KEYS.history(), limit],
    queryFn: () => PerformanceReviewApi.getReviewHistory(limit),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to generate a new review
 */
export function useGenerateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (force: boolean = false) =>
      PerformanceReviewApi.generateReview(force),
    onSuccess: (data) => {
      // Update current review cache
      queryClient.setQueryData(REVIEW_KEYS.current(), data);
      // Invalidate eligibility (has_report_this_month changed)
      queryClient.invalidateQueries({ queryKey: REVIEW_KEYS.eligibility() });
      // Invalidate history
      queryClient.invalidateQueries({ queryKey: REVIEW_KEYS.history() });
    },
  });
}

/**
 * Combined hook for performance review feature
 */
export function usePerformanceReview(userId: string | undefined) {
  const eligibilityQuery = useReviewEligibility(userId);
  const currentReviewQuery = useCurrentReview(userId);
  const generateMutation = useGenerateReview();

  return {
    // Eligibility
    eligibility: eligibilityQuery.data as EligibilityStatus | undefined,
    isEligibilityLoading: eligibilityQuery.isLoading,
    eligibilityError: eligibilityQuery.error,
    refetchEligibility: eligibilityQuery.refetch,

    // Current review
    currentReview: currentReviewQuery.data as PerformanceReview | null | undefined,
    isReviewLoading: currentReviewQuery.isLoading,
    reviewError: currentReviewQuery.error,
    refetchReview: currentReviewQuery.refetch,

    // Generate mutation
    generateReview: generateMutation.mutate,
    generateReviewAsync: generateMutation.mutateAsync,
    isGenerating: generateMutation.isPending,
    generateError: generateMutation.error,

    // Combined state
    isLoading: eligibilityQuery.isLoading || currentReviewQuery.isLoading,
    hasReport: !!currentReviewQuery.data,
  };
}

/**
 * Get month name from number
 */
export function getMonthName(month: number): string {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return months[month - 1] || "Unknown";
}

/**
 * Format period string (e.g., "January 2026")
 */
export function formatPeriod(month: number, year: number): string {
  return `${getMonthName(month)} ${year}`;
}

